import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography } from '@mui/material';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fetch road-routed path coordinates using public OSRM API
const fetchRoute = async (start, end) => {
    if (!start || !end) return [];
    try {
        const url = `https://router.project-osrm.org/route/v1/driving/${start[1]},${start[0]};${end[1]},${end[0]}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.code === 'Ok' && data.routes && data.routes[0]) {
            return data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
        }
    } catch (err) {
        console.error('Error fetching road route from OSRM:', err);
    }
    return [start, end]; // Fallback to straight line journey
};

// Custom icons using Leaflet DivIcon for premium styling
const StoreIcon = L.divIcon({
    html: `<div style="background-color: #2196f3; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
             <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
           </div>`,
    className: 'custom-map-icon',
    iconSize: [36, 36],
    iconAnchor: [18, 18]
});

const HomeIcon = L.divIcon({
    html: `<div style="background-color: #4caf50; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
             <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path><circle cx="12" cy="10" r="3"></circle></svg>
           </div>`,
    className: 'custom-map-icon',
    iconSize: [36, 36],
    iconAnchor: [18, 18]
});

const RiderIcon = L.divIcon({
    html: `<div class="rider-marker-container" style="background-color: #ff9800; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 4px 15px rgba(0,0,0,0.4);">
             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13" rx="2" ry="2"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>
           </div>`,
    className: 'custom-map-icon-rider',
    iconSize: [40, 40],
    iconAnchor: [20, 20]
});

// Auto bounds adjustment component
function MapBoundsController({ pickup, destination, rider }) {
    const map = useMap();

    useEffect(() => {
        const points = [];
        if (pickup && pickup[0] && pickup[1]) points.push(pickup);
        if (destination && destination[0] && destination[1]) points.push(destination);
        if (rider && rider[0] && rider[1]) points.push(rider);

        if (points.length > 0) {
            map.fitBounds(points, {
                padding: [50, 50],
                maxZoom: 16
            });
        }
    }, [pickup, destination, rider, map]);

    return null;
}

MapBoundsController.propTypes = {
    pickup: PropTypes.arrayOf(PropTypes.number),
    destination: PropTypes.arrayOf(PropTypes.number),
    rider: PropTypes.arrayOf(PropTypes.number)
};

export default function RiderTrackerMap({ pickup, destination, rider, height = 400 }) {
    // Check if we have valid coordinates
    const hasPickup = pickup && pickup[0] && pickup[1];
    const hasDestination = destination && destination[0] && destination[1];
    const hasRider = rider && rider[0] && rider[1];

    // Fallback center if everything fails
    const mapCenter = hasPickup ? pickup : [12.0022, 8.5920];

    const [pickupToRiderRoute, setPickupToRiderRoute] = useState([]);
    const [riderToDestRoute, setRiderToDestRoute] = useState([]);
    const [pickupToDestRoute, setPickupToDestRoute] = useState([]);

    useEffect(() => {
        if (hasPickup && hasRider) {
            fetchRoute(pickup, rider).then(setPickupToRiderRoute);
        } else {
            setPickupToRiderRoute([]);
        }
    }, [pickup, rider, hasPickup, hasRider]);

    useEffect(() => {
        if (hasRider && hasDestination) {
            fetchRoute(rider, destination).then(setRiderToDestRoute);
        } else {
            setRiderToDestRoute([]);
        }
    }, [rider, destination, hasRider, hasDestination]);

    useEffect(() => {
        if (hasPickup && hasDestination && !hasRider) {
            fetchRoute(pickup, destination).then(setPickupToDestRoute);
        } else {
            setPickupToDestRoute([]);
        }
    }, [pickup, destination, hasPickup, hasRider, hasDestination]);

    return (
        <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: 3, position: 'relative' }}>
            {/* Inline styles for pulse animation */}
            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes rider-pulse {
                    0% {
                        box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
                    }
                    70% {
                        box-shadow: 0 0 0 12px rgba(255, 152, 0, 0);
                    }
                    100% {
                        box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
                    }
                }
                .rider-marker-container {
                    animation: rider-pulse 1.8s infinite;
                }
            `}} />

            <Box height={height} width="100%" sx={{ zIndex: 1 }}>
                <MapContainer center={mapCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    
                    {hasPickup && (
                        <Marker position={pickup} icon={StoreIcon} />
                    )}

                    {hasDestination && (
                        <Marker position={destination} icon={HomeIcon} />
                    )}

                    {hasRider && (
                        <Marker position={rider} icon={RiderIcon} />
                    )}

                    {/* Draw Polylines */}
                    {hasPickup && hasDestination && !hasRider && (
                        <Polyline 
                            positions={pickupToDestRoute.length > 0 ? pickupToDestRoute : [pickup, destination]} 
                            color="#2196f3" 
                            dashArray="5, 10" 
                            weight={4} 
                        />
                    )}

                    {hasPickup && hasRider && (
                        <Polyline 
                            positions={pickupToRiderRoute.length > 0 ? pickupToRiderRoute : [pickup, rider]} 
                            color="#ff9800" 
                            weight={4} 
                        />
                    )}

                    {hasRider && hasDestination && (
                        <Polyline 
                            positions={riderToDestRoute.length > 0 ? riderToDestRoute : [rider, destination]} 
                            color="#4caf50" 
                            dashArray="5, 10" 
                            weight={4} 
                        />
                    )}

                    <MapBoundsController pickup={pickup} destination={destination} rider={rider} />
                </MapContainer>
            </Box>

            {/* Custom map legend overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 2,
                    p: 1.5,
                    boxShadow: 2,
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                }}
            >
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    LEGEND
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2196f3' }} />
                    <Typography variant="caption">Pickup / Store</Typography>
                </Box>
                {hasRider && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff9800' }} />
                        <Typography variant="caption">Rider (Live)</Typography>
                    </Box>
                )}
                <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#4caf50' }} />
                    <Typography variant="caption">Destination</Typography>
                </Box>
            </Box>
        </Paper>
    );
}

RiderTrackerMap.propTypes = {
    pickup: PropTypes.arrayOf(PropTypes.number),
    destination: PropTypes.arrayOf(PropTypes.number),
    rider: PropTypes.arrayOf(PropTypes.number),
    height: PropTypes.number
};
